<template>
  <div class="lp-element-panel lp-text-panel">
    <info-banner
        v-if="isMultiPropertySelection"
        icon="layers"
        class="lp-text-panel__banner">Editing {{ getSelectedObjectsList.length }} text boxes</info-banner>

    <!-- Typography -->
    <field-group
        label="Typography"
        compact>
      <font-family-select
          :value="getFontFamily"
          :product="product"
          label="Font family"
          variant="panel"/>
      <div class="lp-pair">
        <div class="lp-pair__item">
          <div class="lp-sub-label">Size</div>
          <size-adjust-button
              :key="getFontSize"
              :value="getFontSize"
              :scale="isMultiPropertySelection ? scaleForText : activeObjectForLeftPanel.scaleX"
              label="Size"
              class="lp-size-stepper"/>
        </div>
        <div class="lp-pair__item">
          <div class="lp-sub-label">Line spacing</div>
          <size-adjust-button
              :key="getLineSpacing"
              :value="getLineSpacing"
              label="Line Spacing"
              class="lp-size-stepper"/>
        </div>
      </div>
      <div
          v-if="isMultiPropertySelection || !activeObjectForLeftPanel.isGradient"
          ref="textColorRow"
          :class="{ 'is-active': $store.getters.getIsOpen }"
          class="lp-text-color">
        <color-field
            :color="getColorLabel"
            :active="$store.getters.getIsOpen"
            @pick="toggleTextColorPicker"/>
      </div>
    </field-group>
    <side-popover
        ref="textColorPopoverEl"
        :open="$store.getters.getIsOpen"
        :top="textColorPopover.top"
        :left="textColorPopover.left"
        :width="303"
        title="Text color"
        @close="$store.commit('setIsOpen', false)"
        @click.native.stop>
      <color-picker
          :color="getColorLabel"
          :selected-objects-list="getSelectedObjectsList"
          :content-list="contentList"
          display-style="inline"
          variant="panel"/>
    </side-popover>

    <!-- Style (single text only) -->
    <field-group
        v-if="!isMultiPropertySelection"
        label="Style">
      <div class="lp-row">
        <segmented-control
            :options="styleOptions"
            :value="activeStyleValues"
            multiple
            class="lp-row__grow"
            @click="onStyleClick"/>
        <button
            v-tooltip="'Emoji'"
            v-if="activeObjectForLeftPanel && activeObjectForLeftPanel.type === 'text' && activeObjectForLeftPanel.isEditorEnable"
            :class="{ 'is-active': showEmojiPicker }"
            type="button"
            class="lp-icon-btn"
            aria-label="Emoji"
            @click="openEmojiPicker">
          <icon name="emojiPickerIcon"/>
        </button>
      </div>
      <emoji-picker
          v-if="showEmojiPicker"
          :style="{ top: emojiPopover.top + 'px', left: emojiPopover.left + 'px' }"
          class="emoji-picker-popup light"
          @emoji-click="onEmojiClick"
      />
    </field-group>

    <!-- Lists & script (hidden for 2+ texts, as before: the old multi block had it under d-none) -->
    <field-group
        v-if="!isMultiPropertySelection"
        label="Lists & script">
      <segmented-control
          :options="listOptions"
          :value="activeListValues"
          multiple
          @click="onListClick"/>
    </field-group>

    <!-- Position on page (multi: before Align text, as in Multiple texts.png) -->
    <field-group
        v-if="isMultiPropertySelection"
        label="Position on page">
      <position-controls
          :horizontal="shownAlignment.horizontal"
          :vertical="shownAlignment.vertical"
          @align="onPositionAlign"/>
    </field-group>

    <!-- Align text -->
    <field-group label="Align text">
      <div class="lp-row">
        <segmented-control
            :options="justifyOptions"
            :value="activeJustify"
            class="lp-row__grow"
            @click="onJustifyClick"/>
        <segmented-control
            :options="verticalAlignOptions"
            :value="activeVerticalAlign"
            class="lp-row__fixed"
            @click="onVerticalAlignClick"/>
      </div>
    </field-group>

    <field-group
        v-if="!isMultiPropertySelection"
        label="Position on page">
      <position-controls
          :horizontal="shownAlignment.horizontal"
          :vertical="shownAlignment.vertical"
          @align="onPositionAlign"/>
    </field-group>

    <!-- Effects -->
    <field-group
        v-if="showTextEffectsPanel"
        label="Effects">
      <option-tiles
          :options="effectOptions"
          :value="activeTextEffect"
          @click="onEffectTile">
        <template
            slot="preview"
            slot-scope="{ option }">
          <none-mark v-if="option.value === 'none'"/>
          <span
              v-else
              :class="'is-' + option.value"
              class="lp-aa">Aa</span>
        </template>
      </option-tiles>
      <div
          v-if="activeObjectForLeftPanel.textShadow.textEffect === 'shadow'"
          class="lp-settings-box">
        <slider-row
            :value="activeObjectForLeftPanel.textShadow.angle"
            :min="-180"
            :max="180"
            :step="1"
            label="Direction"
            unit="°"
            @input="updateTextShadowProperty('angle', parseInt($event, 10))"/>
        <slider-row
            :value="activeObjectForLeftPanel.textShadow.distance"
            :display="Math.round(activeObjectForLeftPanel.textShadow.distance * 50)"
            :min="0"
            :max="2"
            :step="0.01"
            label="Distance"
            @input="updateTextShadowProperty('distance', parseFloat($event))"/>
        <slider-row
            :value="activeObjectForLeftPanel.textShadow.shadowBlur"
            :display="Math.round(activeObjectForLeftPanel.textShadow.shadowBlur * 50)"
            :min="0"
            :max="2"
            :step="0.01"
            label="Blur"
            @input="updateTextShadowProperty('shadowBlur', $event)"/>
        <div class="lp-settings-box__row">
          <span class="lp-settings-box__label">Color</span>
          <color-field
              :color="activeObjectForLeftPanel.textShadow.shadowColor"
              :active="isColorPickerOpen"
              class="lp-settings-box__color"
              @pick="openEffectColorPicker"/>
        </div>
      </div>
      <div
          v-if="activeObjectForLeftPanel.outLine.textEffect === 'outline'"
          class="lp-settings-box">
        <slider-row
            :value="activeObjectForLeftPanel.outLine.strokeWidth"
            :min="1"
            :max="10"
            :step="1"
            label="Thickness"
            @input="updateTextOutlineProperty('strokeWidth', $event)"/>
        <div class="lp-settings-box__row">
          <span class="lp-settings-box__label">Color</span>
          <color-field
              :color="activeObjectForLeftPanel.outLine.strokeColor"
              :active="isColorPickerOpen"
              class="lp-settings-box__color"
              @pick="openEffectColorPicker"/>
        </div>
      </div>
      <side-popover
          ref="effectColorPopoverEl"
          :open="isColorPickerOpen && !!effectColorKind"
          :top="effectColorPopover.top"
          :left="effectColorPopover.left"
          :width="303"
          :title="effectColorKind === 'outline' ? 'Outline color' : 'Shadow color'"
          @close="isColorPickerOpen = false"
          @click.native.stop>
        <color-picker
            :key="effectColorKind"
            :active-object="activeObjectForLeftPanel"
            :color="effectColorKind === 'outline' ? activeObjectForLeftPanel.outLine.strokeColor : activeObjectForLeftPanel.textShadow.shadowColor"
            :is-for-shadow-color="true"
            :selected-objects-list="getSelectedObjectsList"
            :content-list="contentList"
            :show-gradient="false"
            :color-type="effectColorType"
            display-style="inline"
            variant="panel"/>
      </side-popover>
    </field-group>

    <!-- Gradient color (single text only) -->
    <field-group
        v-if="!isMultiPropertySelection && showTextEffectsPanel"
        label="Gradient color">
      <option-tiles
          :options="gradientOptions"
          :columns="effectOptions.length"
          :value="activeObjectForLeftPanel.isGradient ? 'gradient' : 'none'"
          @click="onGradientTile">
        <template
            slot="preview"
            slot-scope="{ option }">
          <none-mark v-if="option.value === 'none'"/>
          <span
              v-else
              class="lp-gradient-chip"/>
        </template>
      </option-tiles>
      <div
          v-if="activeObjectForLeftPanel.isGradient"
          class="lp-text-panel__gradient">
        <color-picker
            :color="getColorLabelShape"
            :type="getObjectType"
            :key="'color-picker-'+((activeObjectForLeftPanel && activeObjectForLeftPanel.Id) || 'none')+'-'+((activeObjectForLeftPanel && activeObjectForLeftPanel.isGradient) || false)+'-'+getSelectedPointOfGradient"
            :active-object="activeObjectForLeftPanel"
            :selected-objects-list="getSelectedObjectsList"
            :content-list="contentList"
            :show-gradient="true"
            :apply-section-highlight="true"
            display-style="inline"
            variant="panel"
            color-type="fill"
            @updateSelectedPointOfGradient="updateSelectedPointOfGradient"
            @updateSelectedPointPosition="updateSelectedPointPosition"
            @changeMode="handleModeChange"
            @updateSelectedGradientAngle="updateSelectedGradientAngle"
        />
      </div>
    </field-group>

    <!-- Shape (single text only) -->
    <field-group
        v-if="!isMultiPropertySelection"
        label="Shape">
      <option-tiles
          :options="shapeOptions"
          :columns="effectOptions.length"
          :value="activeObjectForLeftPanel.shapeType === 'curve' ? 'curve' : 'straight'"
          @click="onShapeTile">
        <template
            slot="preview"
            slot-scope="{ option }">
          <span
              v-if="option.value === 'straight'"
              class="lp-shape-straight">Straight</span>
          <svg
              v-else
              class="lp-shape-curve"
              viewBox="0 0 64 28"
              aria-hidden="true">
            <path
                id="lp-text-curve-path"
                d="M6 23 Q32 -1 58 23"
                fill="none"/>
            <path
                class="lp-shape-curve__guide"
                d="M6 26 Q32 2 58 26"/>
            <text>
              <textPath
                  href="#lp-text-curve-path"
                  startOffset="50%"
                  text-anchor="middle">Curve</textPath>
            </text>
          </svg>
        </template>
      </option-tiles>
      <div
          v-if="activeObjectForLeftPanel.shapeType === 'curve'"
          class="lp-settings-box">
        <slider-row
            :value="tempTextCurveValue"
            :display="activeObjectForLeftPanel.curve"
            :min="-100"
            :max="100"
            :step="1"
            label="Radius"
            no-keys
            @input="updateTextCurveProperty(String($event))"
            @slide-start="radiusMouseDown()"
            @slide-end="radiusMouseUp()"/>
      </div>
    </field-group>
  </div>
</template>

<script>
import 'emoji-picker-element';
import tinymce from 'tinymce';
import twemoji from 'twemoji';
import EventBus from '@/EventBus';
import ApplicationEventTypes from '@/mixins/ApplicationEventTypes';
import { TinyMCECommandTypes, TextCommandTypes } from '@/mixins/TextCommandTypes';
import TextCurveMixin from '@/mixins/TextCurveMixin';
import { decodeStoredHtml, removeInlineColors, removeInlineColorsFromNode } from '@/components/Helper';
import { findObjectById } from '@/components/Canvas/utils/groupHelpers';
import FontFamilySelector from '@/components/FontFamilySelector/FontFamilySelector';
import SizeAdjustButton from '@/components/SizeAdjustButton/SizeAdjustButton';
import ColorPicker from '@/components/ColorPicker/ColorPicker';
import panelContext from '@/components/LeftSection/mixins/panelContext';
import objectAlignment from '@/components/LeftSection/mixins/objectAlignment';
import objectEffects from '@/components/LeftSection/mixins/objectEffects';
import gradientFill from '@/components/LeftSection/mixins/gradientFill';
import sidePopover from '@/components/LeftSection/mixins/sidePopover';
import FieldGroup from '@/components/LeftSection/ui/FieldGroup';
import SegmentedControl from '@/components/LeftSection/ui/SegmentedControl';
import PositionControls from '@/components/LeftSection/ui/PositionControls';
import OptionTiles from '@/components/LeftSection/ui/OptionTiles';
import SliderRow from '@/components/LeftSection/ui/SliderRow';
import ColorField from '@/components/LeftSection/ui/ColorField';
import InfoBanner from '@/components/LeftSection/ui/InfoBanner';
import SidePopover from '@/components/LeftSection/ui/SidePopover';
import NoneMark from '@/components/LeftSection/ui/NoneMark';

const EFFECT_OPTIONS   = [
  { value: 'none', label: 'None' },
  { value: 'shadow', label: 'Shadow' },
  { value: 'outline', label: 'Outline' },
];
const GRADIENT_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'gradient', label: 'Gradient' },
];
const SHAPE_OPTIONS    = [
  { value: 'straight', label: 'Straight' },
  { value: 'curve', label: 'Curve' },
];
const VERTICAL_ALIGN   = [
  { value: 'top', icon: 'textaligntop', tooltip: 'Top' },
  { value: 'middle', icon: 'textaligncenter', tooltip: 'Middle' },
  { value: 'bottom', icon: 'text-align-bottom', tooltip: 'Bottom' },
];

export default {
  name:       'TextPanel',
  components: {
    'font-family-select': FontFamilySelector,
    'size-adjust-button': SizeAdjustButton,
    'color-picker':       ColorPicker,
    'field-group':        FieldGroup,
    'segmented-control':  SegmentedControl,
    'position-controls':  PositionControls,
    'option-tiles':       OptionTiles,
    'slider-row':         SliderRow,
    'color-field':        ColorField,
    'info-banner':        InfoBanner,
    'side-popover':       SidePopover,
    'none-mark':          NoneMark,
  },
  mixins: [TextCommandTypes.Mixin, TinyMCECommandTypes.Mixin, TextCurveMixin, panelContext, objectAlignment, objectEffects, gradientFill, sidePopover],
  props:  {
    width:       { type: Number, default: 0 },
    height:      { type: Number, default: 0 },
    productCrop: { type: Object, default: null },
  },
  data() {
    return {
      tempTextCurveValue:   40,
      showEmojiPicker:      false,
      textSelectionCount:   0,
      effectOptions:        EFFECT_OPTIONS,
      gradientOptions:      GRADIENT_OPTIONS,
      shapeOptions:         SHAPE_OPTIONS,
      verticalAlignOptions: VERTICAL_ALIGN,
      textColorPopover:     { top: 0, left: 0 },
      effectColorPopover:   { top: 0, left: 0 },
      emojiPopover:         { top: 0, left: 0 },
    };
  },
  computed: {
    showTextEffectsPanel() {
      void this.textSelectionCount;

      if (!this.activeObjectForLeftPanel || this.activeObjectForLeftPanel.type !== 'text') {
        return true;
      }

      const selectedTexts = (this.selectedObjectsList || []).filter(o => o && o.type === 'text');
      if (selectedTexts.length > 1) {
        return !selectedTexts.every(o => this.isOnlyEmojiHtml(o.Id));
      }

      const id = this.activeObjectForLeftPanel.Id;
      return !(this.isOnlyEmojiHtml(id) || this.isOnlyEmojiSelection(id));
    },
    getLinearGradientNumber() {
      const outline = this.activeObjectForLeftPanel && this.activeObjectForLeftPanel.outLine;
      return outline ? (((outline.strokeWidth - 0.2) / 1.8 * 9)) * 2 : 0;
    },
    getFontSize() {
      return (this.activeObjectForLeftPanel) && parseFloat(this.activeObjectForLeftPanel.valuesForLeftPanel.fontSize);
    },
    getFontFamily() {
      return (this.activeObjectForLeftPanel) && this.activeObjectForLeftPanel.valuesForLeftPanel.fontFamily;
    },
    isEditorEnable() {
      return !this.activeObjectForLeftPanel.isEditorEnable;
    },
    getColorLabel() {
      return (this.activeObjectForLeftPanel) && this.activeObjectForLeftPanel.valuesForLeftPanel.color;
    },
    getFontStyleBold: {
      get() {
        return this.activeObjectForLeftPanel.valuesForLeftPanel.isBold;
      },
      set(newValue) {
        this.activeObjectForLeftPanel.valuesForLeftPanel.isBold = newValue;
      },
    },
    getFontStyleItalic: {
      get() {
        return this.activeObjectForLeftPanel.valuesForLeftPanel.isItalic;
      },
      set(newValue) {
        this.activeObjectForLeftPanel.valuesForLeftPanel.isItalic = newValue;
      },
    },
    getFontStyleUnderline: {
      get() {
        return this.activeObjectForLeftPanel.valuesForLeftPanel.isUnderline;
      },
      set(newValue) {
        this.activeObjectForLeftPanel.valuesForLeftPanel.isUnderline = newValue;
      },
    },
    getLineSpacing() {
      return (this.activeObjectForLeftPanel) && parseFloat(this.activeObjectForLeftPanel.valuesForLeftPanel.lineSpacing);
    },
    getBulletList() {
      return this.activeObjectForLeftPanel.valuesForLeftPanel.isBulletList;
    },
    getNumberList: {
      get() {
        return this.activeObjectForLeftPanel.valuesForLeftPanel.isNumberList;
      },
      set(newValue) {
        console.log(newValue);
      },
    },
    getSupperScript: {
      get() {
        return this.activeObjectForLeftPanel.valuesForLeftPanel.isSuperScript;
      },
    },
    getSubScript: {
      get() {
        return this.activeObjectForLeftPanel.valuesForLeftPanel.isSubScript;
      },
    },
    scaleForText() {
      let scale = this.activeObjectForLeftPanel ? this.activeObjectForLeftPanel.scaleX : 1.0;
      if (this.selectedObjectsList.length > 1) {
        scale = 1.0;
      }
      return scale;
    },
    styleOptions() {
      const options = [
        {
          value:    'bold', icon:     'boldicon', tooltip:  'Toggle Bold Text', disabled: this.isEditorEnable,
        },
        {
          value:    'italic', icon:     'Italic', tooltip:  'Italic', disabled: this.isEditorEnable,
        },
      ];
      if (!this.activeObjectForLeftPanel.isGradient) {
        options.push({
          value:    'underline', icon:     'underlineicon', tooltip:  'Underline', disabled: this.isEditorEnable,
        });
      }
      return options;
    },
    activeStyleValues() {
      return [
        this.getFontStyleBold && 'bold',
        this.getFontStyleItalic && 'italic',
        this.getFontStyleUnderline && 'underline',
      ].filter(Boolean);
    },
    listOptions() {
      return [
        {
          value:    'bullet', icon:     'list-bullets', tooltip:  'Bullet', disabled: this.isEditorEnable,
        },
        {
          value:    'number', icon:     'list-numbers', tooltip:  'List', disabled: this.isEditorEnable,
        },
        {
          value:    'superscript', icon:     'superscript', tooltip:  'Superscript', disabled: this.isEditorEnable,
        },
        {
          value:    'subscript', icon:     'subscript', tooltip:  'Subscript', disabled: this.isEditorEnable,
        },
      ];
    },
    activeListValues() {
      return [
        this.getBulletList && 'bullet',
        this.getNumberList && 'number',
        this.getSupperScript && 'superscript',
        this.getSubScript && 'subscript',
      ].filter(Boolean);
    },
    justifyOptions() {
      return [
        { value: 'left', icon: 'text-horizontal-align-left', tooltip: 'Left' },
        { value: 'center', icon: 'text-horizontal-align-center', tooltip: 'Center' },
        { value: 'right', icon: 'text-horizontal-align-right', tooltip: 'Right' },
        { value: 'justify', icon: 'justify-text', tooltip: 'Justify' },
      ];
    },
    activeJustify() {
      const values = this.activeObjectForLeftPanel.valuesForLeftPanel;
      if (values.isJustifyLeft) { return 'left'; }
      if (values.isJustifyCenter) { return 'center'; }
      if (values.isJustifyRight) { return 'right'; }
      if (values.isJustify) { return 'justify'; }
      return null;
    },
    activeVerticalAlign() {
      return this.activeObjectForLeftPanel.verticalAlign.toLowerCase();
    },
    activeTextEffect() {
      const object = this.activeObjectForLeftPanel;
      if (object.textShadow.textEffect === 'shadow') { return 'shadow'; }
      if (object.outLine.textEffect === 'outline') { return 'outline'; }
      if (object.textShadow.textEffect === 'none' && object.outLine.textEffect === 'none') { return 'none'; }
      return null;
    },
    // Which colour the Effects colour popover edits
    effectColorKind() {
      if (this.activeObjectForLeftPanel.textShadow.textEffect === 'shadow') { return 'shadow'; }
      if (this.activeObjectForLeftPanel.outLine.textEffect === 'outline') { return 'outline'; }
      return null;
    },
    // Same colour-type values the old single / multi colour pickers used
    effectColorType() {
      return this.effectColorKind === 'outline' && this.isMultiPropertySelection ? 'outline' : 'text-shadow';
    },
  },
  watch: {
    // Text part of LeftSection's activeObject watcher (moved, Stage 2)
    activeObject: {
      handler(newVal) {
        const list   = this.getSelectedObjectsList;
        let panelObj = newVal;
        if (newVal && newVal.type === 'group' && list && list.length) {
          const withEffect = list.find(obj => obj
              && obj.effect
              && obj.effect !== 'none'
              && obj.effect !== '');
          panelObj         = withEffect || list[0];
        } else if (newVal && newVal.type !== 'group') {
          panelObj = findObjectById(this.contentList, newVal.Id) || newVal;
        }
        if (panelObj && panelObj.type === 'text') {
          this.tempTextCurveValue = panelObj.curve ? panelObj.curve : 40;
        }
      },
      immediate: true,
      deep:      true,
    },
    '$store.getters.getIsOpen': {
      handler(open) {
        if (!open) { return; }
        this.isColorPickerOpen = false; // only one colour popover at a time
        this.$nextTick(() => {
          this.textColorPopover = this.sidePopoverPosition(this.$refs.textColorRow, { width: 303, height: 520 });
          this.textColorPopover = this.fitSidePopover(this.$refs.textColorPopoverEl, this.textColorPopover);
        });
      },
      immediate: true,
    },
  },
  methods: {
    changeFontSize(font, buttonType = '') { // buttonType is for increment or decrement buttons
      font && this.triggerUpdateTextProperties({
        command:     this.TinyMCECommandTypes.Size,
        value:       `${font}px`,
        commandType: this.TextCommandTypes.TinyMceCommand,
        buttonType,
      });
      this.$nextTick(() => {
        setTimeout(() => {
          this.curveTextValues();
        }, 80);
      });
    },
    changeFontFamily(font) {
      font && this.triggerUpdateTextProperties({
        command:     this.TinyMCECommandTypes.FontFamily,
        value:       font,
        commandType: this.TextCommandTypes.TinyMceCommand,
      });
    },
    changeFontColor(rgb) {
      rgb && this.triggerUpdateTextProperties({
        command:     this.TinyMCECommandTypes.Color,
        value:       rgb.toString(),
        commandType: this.TextCommandTypes.TinyMceCommand,
      });
    },
    triggerUpdateTextProperties(payload) {
      payload && EventBus.$emit(ApplicationEventTypes.TextObjectEvents.UPDATED, payload);
    },
    changeCharacterSpacing(lineSpace) {
      lineSpace && this.triggerUpdateTextProperties({
        command:     this.TinyMCECommandTypes.LineSpacing,
        value:       lineSpace,
        commandType: this.TextCommandTypes.TinyMceCommand,
      });
      this.$nextTick(() => {
        setTimeout(() => {
          this.curveTextValues();
        }, 80);
      });
    },
    updateTextShadowProperty(property, value) {
      this.getSelectedTextObjectsForEffects().forEach((object) => {
        this.updateTextShadowPropertyById(property, value, object.Id);
      });

      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);
      this.emitUndoRedoRequest('updateTextShadowProperty');
    },
    updateTextShadowPropertyById(property, value, id) {
      const selectedObject = findObjectById(this.contentList, id);
      if (!selectedObject || selectedObject.type !== 'text') { return; }
      const shadow = selectedObject.textShadow;

      const fontSize = parseInt(selectedObject.fontSize, 10) || 16;

      if (property === 'angle' || property === 'distance') {
        shadow.angle    = property === 'angle' ? value : shadow.angle;
        shadow.distance = property === 'distance' ? value : shadow.distance;

        const scaledDistance = shadow.distance * (fontSize / 16);

        const radians = (shadow.angle * Math.PI) / 180;

        shadow.shadowOffsetX = scaledDistance * Math.cos(radians);
        shadow.shadowOffsetY = scaledDistance * Math.sin(radians);
      } else {
        shadow[property] = property !== 'shadowColor' ? parseFloat(value) : value;
      }
      console.log(shadow);
      selectedObject.textShadow = shadow;
    },
    updateTextOutlineProperty(property, value) {
      this.getSelectedTextObjectsForEffects().forEach((object) => {
        object.outLine[property] = property != 'strokeColor' ? parseFloat(value) : value;
      });
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);
      this.emitUndoRedoRequest('updateTextOutlineProperty');
    },
    removeTextEffect() {
      const selectedTextObjects = this.getSelectedTextObjectsForEffects();
      selectedTextObjects.forEach((object) => {
        this.removeTextEffectById(object.Id);
      });

      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);
      EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
      this.restoreSelectionAfterChange(selectedTextObjects);
      this.emitUndoRedoRequest('removeTextEffect');
    },
    removeTextEffectById(id) {
      const selectedObject = findObjectById(this.contentList, id);
      if (!selectedObject || selectedObject.type !== 'text') { return; }
      selectedObject.textShadow = {
        textEffect:    'none',
        angle:         0,
        distance:      0,
        shadowBlur:    0,
        shadowColor:   'rgb(252,252,252)',
        transparency:  0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
      };

      selectedObject.outLine = {
        textEffect:  'none',
        strokeWidth: 0,
        strokeColor: 'rgb(252,252,252)',
      };

      if (this.selectedObjectsList.length == 1 && selectedObject.type == 'text' && selectedObject.shapeType && selectedObject.shapeType == 'curve' && selectedObject.isEditorEnable) {
        selectedObject.isEditorEnable = false;
        const selector                = `.canvas-non-play-all [id='${selectedObject.Id}'] > div`;
        const elem                    = document.querySelector(selector);
        selectedObject.unCurvedHtml   = elem.innerHTML;
        this.traverseAndWrap(elem);
        this.curveText(elem, selectedObject.curve);
        setTimeout(() => {
          const newDimensions = this.adjustTargetElement(`.canvas-non-play-all [id='${selectedObject.Id}']`, selectedObject);

          const updatedHtml = elem.innerHTML;

          selectedObject.Value        = updatedHtml;
          selectedObject.modifiedHtml = updatedHtml;
          selectedObject.curvedHtml   = updatedHtml;

          this.applyCurvedBoundsToTextObject(this.contentList, selectedObject, newDimensions);
        });
      }
    },
    isOnlyEmojiText(text) {
      if (!text) { return false; }
      return /^(?:(?:\p{Extended_Pictographic})(?:\p{Emoji_Modifier}|\uFE0F|\u200D|\p{Emoji_Component})*|(?:[0-9#*]\uFE0F?\u20E3))+$/u.test(text);
    },
    isOnlyEmojiHtml(id) {
      const selectedObject = findObjectById(this.contentList, id);
      if (!selectedObject || !selectedObject.modifiedHtml) { return false; }

      const wrapper     = document.createElement('div');
      wrapper.innerHTML = selectedObject.modifiedHtml;

      const text = (wrapper.textContent || '').replace(/\s/g, '');

      if (text.length > 0) {
        return this.isOnlyEmojiText(text);
      }
      return wrapper.querySelectorAll('img.emoji').length > 0;
    },
    isOnlyEmojiSelection(objectId) {
      try {
        if (!objectId) { return false; }
        const ed = tinymce.get(`editor-${objectId}`);
        if (!ed || !ed.selection) { return false; }

        const html = (ed.selection.getContent({ format: 'html' }) || '').trim();
        const text = (ed.selection.getContent({ format: 'text' }) || '').replace(/\s/g, '');

        if (text.length > 0) {
          return this.isOnlyEmojiText(text);
        }

        if (!html) { return false; }

        const wrapper     = document.createElement('div');
        wrapper.innerHTML = html;

        const selectionText = (wrapper.textContent || '').replace(/\s/g, '');
        if (selectionText.length > 0) { return false; }

        return wrapper.querySelectorAll('img.emoji').length > 0;
      } catch (e) {
        return false;
      }
    },
    applyShadowOnText() {
      const selectedTextObjects = this.getSelectedTextObjectsForEffects();
      selectedTextObjects.forEach((object) => {
        this.applyShadowOnTextById(object.Id);
      });
      // if (this.activeObjectForLeftPanel.isEditorEnable) { return; }

      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);
      EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
      this.restoreSelectionAfterChange(selectedTextObjects);
      if (this.activeObjectForLeftPanel && this.activeObjectForLeftPanel.type === 'text') {
        setTimeout(() => {
          this.curveTextValueById(this.activeObjectForLeftPanel.Id);
        });
      }
      this.emitUndoRedoRequest('Apply Shadow on Text');
    },
    applyShadowOnTextById(id) {
      const selectedObject = findObjectById(this.contentList, id);
      if (!selectedObject || selectedObject.type !== 'text') { return; }

      selectedObject.textShadow = {
        textEffect:    'shadow',
        angle:         0,
        distance:      0.75,
        shadowBlur:    0,
        shadowColor:   'rgb(132,108,99)',
        transparency:  0,
        shadowOffsetX: 0.75,
        shadowOffsetY: 0,
      };

      selectedObject.outLine = {
        textEffect:  'none',
        strokeWidth: 0,
        strokeColor: 'rgb(252,252,252)',
      };
    },
    applyGradientOnText() {
      this.removeGradientOnText({ restoreSelection: false });
      const self            = this;
      const selectedObjects = this.getSelectedObjectsList;
      if (selectedObjects.length > 0) {
        selectedObjects.forEach((object) => {
          if (object.type === 'text') {
            this.applyGradientOnTextById(object.Id, true);
          }
        });
      } else if (this.activeObjectForLeftPanel && this.activeObjectForLeftPanel.type === 'text') {
        this.applyGradientOnTextById(this.activeObjectForLeftPanel.Id, true);
      }
      if (this.activeObjectForLeftPanel && this.activeObjectForLeftPanel.type === 'text') {
        self.curveTextValueById(this.activeObjectForLeftPanel.Id);
      }
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);
      this.restoreSelectionAfterChange();
      this.emitUndoRedoRequest('Adding Gradient');
    },
    applyGradientOnTextById(id, doEnable = true) {
      const selectedObject = findObjectById(this.contentList, id);
      if (!selectedObject || selectedObject.type !== 'text') { return; }
      selectedObject.isGradient = doEnable;
      if (doEnable) {
        this.clearTextColorsForGradient(selectedObject);
      }
    },
    removeGradientOnText({ restoreSelection = true } = {}) {
      this.emitUndoRedoRequest('Removing Gradient');
      const selectedObjects = this.getSelectedObjectsList;
      if (selectedObjects.length > 0) {
        selectedObjects.forEach((object) => {
          if (object.type === 'text') {
            this.applyGradientOnTextById(object.Id, false);
          }
        });
      } else if (this.activeObjectForLeftPanel && this.activeObjectForLeftPanel.type === 'text') {
        this.applyGradientOnTextById(this.activeObjectForLeftPanel.Id, false);
      }

      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);
      EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
      if (restoreSelection) {
        this.restoreSelectionAfterChange();
      }
      this.emitUndoRedoRequest('removeGradientOnText');
    },
    clearTextColorsForGradient(selectedObject) {
      ['Value', 'modifiedHtml', 'curvedHtml'].forEach((key) => {
        if (typeof selectedObject[key] !== 'string' || !selectedObject[key]) { return; }
        selectedObject[key] = removeInlineColors(selectedObject[key]);
      });

      if (selectedObject.unCurvedHtml) {
        selectedObject.unCurvedHtml = removeInlineColors(decodeStoredHtml(selectedObject.unCurvedHtml));
      }

      const elem = document.querySelector(`.canvas-non-play-all [id='${selectedObject.Id}'] .focus-text-div`);
      if (elem) {
        removeInlineColorsFromNode(elem);
      }
    },
    applyOutlineOnText() {
      const selectedTextObjects = this.getSelectedTextObjectsForEffects();
      selectedTextObjects.forEach((object) => {
        this.applyOutlineOnTextById(object.Id);
      });

      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);
      EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
      this.restoreSelectionAfterChange(selectedTextObjects);
      if (this.activeObjectForLeftPanel && this.activeObjectForLeftPanel.type === 'text') {
        setTimeout(() => {
          this.curveTextValueById(this.activeObjectForLeftPanel.Id);
        });
      }
      this.emitUndoRedoRequest('Apply Outline on Text');
    },
    applyOutlineOnTextById(id) {
      const selectedObject = findObjectById(this.contentList, id);
      if (!selectedObject || selectedObject.type !== 'text') { return; }

      selectedObject.textShadow = {
        textEffect:    'none',
        angle:         0,
        distance:      0,
        shadowBlur:    0,
        shadowColor:   'rgb(252,252,252)',
        transparency:  0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
      };

      selectedObject.outLine = {
        textEffect:  'outline',
        strokeWidth: 5,
        strokeColor: '#ff0000',
      };
    },
    updateTextCurveProperty(value) {
      this.getSelectedObjectsList.forEach((object) => {
        if (object.type === 'text') {
          this.tempTextCurveValue = value;
          this.updateTextCurvePropertyById(object.Id, value);
        }
      });
    },
    updateTextCurvePropertyById(objectId, value) {
      const selectedObject                = findObjectById(this.contentList, objectId);
      this.activeObjectForLeftPanel.curve = value;
      selectedObject.curve                = value;
      if (selectedObject.isEditorEnable) {
        selectedObject.isEditorEnable = false;
        selectedObject.unCurvedHtml   = selectedObject.modifiedHtml;
      }
      const selector = `.canvas-non-play-all [id='${selectedObject.Id}'] > div`;
      const elem     = document.querySelector(selector);
      this.traverseAndWrap(elem);
      this.curveText(elem, parseInt(value, 10), selectedObject);
      this.emitUndoRedoRequest('After curve property change');
    },
    applyCurveOnText() {
      const selectedObjects = this.getSelectedObjectsList;
      selectedObjects.forEach((object) => {
        if (object.type === 'text') {
          this.applyCurveOnTextById(object.Id);
        }
      });
      this.restoreSelectionAfterChange(selectedObjects);
    },
    applyCurveOnTextById(id) {
      console.log('Apply Curve On Text By Id');
      const self           = this;
      const selectedObject = findObjectById(this.contentList, id);

      if (!selectedObject) {
        return;
      }
      document.querySelector(`.canvas-non-play-all [id='${selectedObject.Id}']`).classList.remove('focus-text-editor');

      const selector = `.canvas-non-play-all [id='${selectedObject.Id}'] .focus-text-div`;
      const elem     = document.querySelector(selector);
      if (!selectedObject.shapeType || selectedObject.shapeType !== 'curve') {
        selectedObject.unCurvedHtml = elem ? elem.innerHTML : selectedObject.Value;

        selectedObject.nonCurvedWidth   = selectedObject.Width;
        selectedObject.nonCurvedHeight  = selectedObject.Height;
        selectedObject.nonCurvedOffsetX = 0;
        selectedObject.nonCurvedOffsetY = 0;
      }
      selectedObject.shapeType      = 'curve';
      selectedObject.curve          = 40;
      selectedObject.isEditorEnable = false;

      this.traverseAndWrap(elem);
      this.curveText(elem, 40, selectedObject);


      // setTimeout(() => {
      const newDimensions         = this.adjustTargetElement(`.canvas-non-play-all [id='${selectedObject.Id}']`, selectedObject);
      const updatedHtml           = elem.innerHTML;
      selectedObject.Value        = updatedHtml;
      selectedObject.modifiedHtml = updatedHtml;
      selectedObject.curvedHtml   = updatedHtml;

      this.applyCurvedBoundsToTextObject(this.contentList, selectedObject, newDimensions);
      selectedObject.verticalAlign = 'top';
      EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
      this.emitUndoRedoRequest('After Curve Text update');
    },
    removeTextShape() {
      this.getSelectedObjectsList.forEach((obj) => {
        if (obj.type === 'text') {
          const selectedObject = findObjectById(this.contentList, obj.Id);
          if (!selectedObject) {
            return;
          }
          selectedObject.shapeType    = 'none';
          selectedObject.curve        = 40;
          let nextValue               = selectedObject.unCurvedHtml
              ? decodeStoredHtml(selectedObject.unCurvedHtml)
              : selectedObject.Value;
          nextValue                   = this.getUncurvedTextHtml(selectedObject, nextValue);
          nextValue                   = this.parseEmojiForTextHtml(nextValue);
          selectedObject.Value        = nextValue;
          selectedObject.modifiedHtml = nextValue;
          selectedObject.unCurvedHtml = nextValue;
          selectedObject.curvedHtml   = '';
        }
      });
      this.emitUndoRedoRequest('Remove Curve From Text');
      EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
      this.restoreSelectionAfterChange();
    },
    getUncurvedTextHtml(selectedObject, currentValue) {
      if (!selectedObject || typeof currentValue !== 'string') {
        return currentValue;
      }
      const hasEmojiInCache = currentValue.includes('class="emoji"') || currentValue.includes('class=emoji');
      if (hasEmojiInCache) {
        return currentValue;
      }
      const selector = `.canvas-non-play-all [id='${selectedObject.Id}'] .focus-text-div`;
      const liveElem = document.querySelector(selector);
      if (!liveElem) {
        return currentValue;
      }
      const hasEmojiInLive = liveElem.querySelector('img.emoji');
      if (!hasEmojiInLive) {
        return currentValue;
      }

      const clone = liveElem.cloneNode(true);
      clone.querySelectorAll('span').forEach((span) => {
        span.style.position        = '';
        span.style.left            = '';
        span.style.marginLeft      = '';
        span.style.transform       = '';
        span.style.transformOrigin = '';
        span.style.removeProperty('--i');
        span.style.removeProperty('--total');
      });
      return clone.innerHTML;
    },
    parseEmojiForTextHtml(value) {
      if (typeof value !== 'string' || value.length === 0) {
        return value;
      }
      const wrapper     = document.createElement('div');
      wrapper.innerHTML = value;
      twemoji.parse(wrapper, {
        className:  'emoji',
        callback:   icon => `https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/${icon}.svg`,
        attributes: () => ({
          draggable:   'false',
          crossorigin: 'anonymous',
          style:       'display:inline-block;width:1em;height:1em;max-width:1em;line-height:1;vertical-align:-0.15em;margin:0 0.05em;pointer-events:none;',
        }),
      });
      return wrapper.innerHTML;
    },
    radiusMouseDown() {
      console.log('mousedown');
      EventBus.$emit(ApplicationEventTypes.Canvas.HIDE_BOUNDING_BOX);
    },
    radiusMouseUp() {
      console.log('mouseup');

      const selectedObject         = findObjectById(this.contentList, this.activeObjectForLeftPanel.Id);
      const selector               = `.canvas-non-play-all [id='${selectedObject.Id}'] .focus-text-div`;
      const elem                   = document.querySelector(selector);
      selectedObject.verticalAlign = parseInt(selectedObject.curve, 10) >= 0 ? 'top' : 'bottom';

      const newDimensions = this.adjustTargetElement(`.canvas-non-play-all [id='${selectedObject.Id}']`, selectedObject);

      const updatedHtml = elem.innerHTML;

      console.log('updatedHtml', [updatedHtml]);

      selectedObject.Value        = updatedHtml;
      selectedObject.modifiedHtml = updatedHtml;
      selectedObject.curvedHtml   = updatedHtml;

      this.applyCurvedBoundsToTextObject(this.contentList, selectedObject, newDimensions);
      // selectedObject.Y      = selectedObject.nonCurvedY - ((newHeight - selectedObject.nonCurvedHeight) / 2);
      // selectedObject.verticalAlign = 'top';
      console.log(newDimensions);
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);


      // const newDimensions2 = this.adjustTargetElement(`.canvas-non-play-all [id='${selectedObject.Id}']`, selectedObject);
      // console.log('newDimensions2', newDimensions2);
      // const scaleFactor     = (this.$store.getters.getZoomLevel / 100);
      // const newWidth        = (newDimensions.width / scaleFactor) / selectedObject.scaleX;
      // const newHeight       = (newDimensions.height / scaleFactor) / selectedObject.scaleY;
      // selectedObject.Width  = newWidth;
      // selectedObject.Height = newHeight * (parseInt(value, 10) > 0 ? 1 : 2);
      // selectedObject.X      = selectedObject.nonCurvedX - ((newWidth - selectedObject.nonCurvedWidth) / 2);
      // // selectedObject.Y      = selectedObject.nonCurvedY - ((newHeight - selectedObject.nonCurvedHeight) / 2);
      // // selectedObject.verticalAlign = 'top';

      setTimeout(() => {
        // EventBus.$emit('onObjectSelectedFromRightPanel', this.activeObjectForLeftPanel.Id);
        // EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);

        if (parseInt(selectedObject.curve, 10) < 0) {
          document.querySelector(`.canvas-non-play-all [id='${selectedObject.Id}'] .focus-text-div`).style.marginBottom = '1.3em';
        } else {
          document.querySelector(`.canvas-non-play-all [id='${selectedObject.Id}'] .focus-text-div`).style.marginBottom = '0';
        }


        // if (this.selectedObjectsList.length > 1) {
        //   setTimeout(() => {
        //     EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.SELECT_MULTIPLE_OBJECTS, this.selectedObjectsList);
        //   });
        // } else {
        setTimeout(() => {
          EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
          this.restoreSelectionAfterChange();
          setTimeout(() => {
            EventBus.$emit(ApplicationEventTypes.Canvas.HIDE_BOUNDING_BOX, false);
          });
        });
        // }
      });
    },
    curveTextValues() {
      this.getSelectedObjectsList.forEach((obj) => {
        const object = findObjectById(this.contentList, obj.Id);
        // Check the text object itself — when a group is selected, activeObject has no isEditorEnable.
        if (
            object
            && object.type === 'text'
            && object.shapeType === 'curve'
            && object.isEditorEnable !== true
        ) {
          object.unCurvedHtml = object.Value.indexOf('transform-origin') === -1 ? object.Value : object.unCurvedHtml;

          const selector = `.canvas-non-play-all [id='${object.Id}'] > div`;
          const elem     = document.querySelector(selector);
          if (!elem) {
            return;
          }
          this.traverseAndWrap(elem);
          this.curveText(elem, parseInt(object.curve, 10));

          setTimeout(() => {
            const newDimensions = this.adjustTargetElement(`.canvas-non-play-all [id='${object.Id}']`, object);

            const updatedHtml = elem.innerHTML;

            object.Value        = updatedHtml;
            object.modifiedHtml = updatedHtml;
            object.curvedHtml   = updatedHtml;

            if (newDimensions) {
              this.applyCurvedBoundsToTextObject(this.contentList, object, newDimensions);
            }
            EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.UPDATED);
            EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);
          });
        }
      });
    },
    curveTextValueById(objectId) {
      const object = findObjectById(this.contentList, objectId);
      if (object && object.type === 'text' && object.shapeType && object.shapeType === 'curve') {
        object.unCurvedHtml = object.Value.indexOf('transform-origin') === -1 ? object.Value : object.unCurvedHtml;

        const selector = `.canvas-non-play-all [id='${object.Id}'] > div`;
        const elem     = document.querySelector(selector);
        if (!elem) {
          return;
        }
        this.traverseAndWrap(elem);
        this.curveText(elem, parseInt(object.curve, 10), object);

        setTimeout(() => {
          const newDimensions = this.adjustTargetElement(`.canvas-non-play-all [id='${object.Id}']`, object);

          const updatedHtml = elem.innerHTML;

          object.Value        = updatedHtml;
          object.modifiedHtml = updatedHtml;
          object.curvedHtml   = updatedHtml;

          if (newDimensions) {
            this.applyCurvedBoundsToTextObject(this.contentList, object, newDimensions);
          }
          EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.UPDATED);
          EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);
        });
      }
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    onEmojiClick(event) {
      const emoji    = event.detail.unicode;
      const targetId = this.activeObjectForLeftPanel.Id;
      const eventId  = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      EventBus.$emit('insertEmoji', { emoji, targetId, eventId });
    },
    onTextSelectionUpdatedForEffects() {
      this.textSelectionCount += 1;
    },
    // ---- new UI (Stage 2): map the design controls onto the moved methods ----
    onStyleClick(option) {
      const commands        = {
        bold:      [this.TinyMCECommandTypes.Bold, this.getFontStyleBold],
        italic:    [this.TinyMCECommandTypes.Italic, this.getFontStyleItalic],
        underline: [this.TinyMCECommandTypes.Underline, this.getFontStyleUnderline],
      };
      const [command, isOn] = commands[option.value];
      this.triggerUpdateTextProperties({ command, value: !isOn, commandType: this.TextCommandTypes.TinyMceCommand });
    },
    onListClick(option) {
      const commands        = {
        bullet:      [this.TinyMCECommandTypes.InsertUnorderedList, this.getBulletList],
        number:      [this.TinyMCECommandTypes.InsertOrderedList, this.getNumberList],
        superscript: [this.TinyMCECommandTypes.Superscript, this.getSupperScript],
        subscript:   [this.TinyMCECommandTypes.Subscript, this.getSubScript],
      };
      const [command, isOn] = commands[option.value];
      this.triggerUpdateTextProperties({ command, value: !isOn, commandType: this.TextCommandTypes.TinyMceCommand });
    },
    onJustifyClick(option) {
      if (option.value === this.activeJustify) {
        return; // like the old radio inputs: clicking the checked option does nothing
      }
      const values          = this.activeObjectForLeftPanel.valuesForLeftPanel;
      const commands        = {
        left:    [this.TinyMCECommandTypes.JustifyLeft, values.isJustifyLeft],
        center:  [this.TinyMCECommandTypes.JustifyCenter, values.isJustifyCenter],
        right:   [this.TinyMCECommandTypes.JustifyRight, values.isJustifyRight],
        justify: [this.TinyMCECommandTypes.JustifyFull, values.isJustify],
      };
      const [command, isOn] = commands[option.value];
      this.triggerUpdateTextProperties({ command, value: !isOn, commandType: this.TextCommandTypes.TinyMceCommand });
    },
    onVerticalAlignClick(option) {
      if (option.value === this.activeVerticalAlign) {
        return;
      }
      this.triggerUpdateTextProperties({
        command:     this.TinyMCECommandTypes.VerticalAlign,
        value:       option.value,
        commandType: this.TextCommandTypes.VerticalAlignmentCommand,
      });
    },
    onEffectTile(option) {
      if (option.value === 'shadow') {
        this.applyShadowOnText();
      } else if (option.value === 'outline') {
        this.applyOutlineOnText();
      } else {
        this.removeTextEffect();
      }
    },
    onGradientTile(option) {
      if (option.value === 'gradient') {
        this.applyGradientOnText();
      } else {
        this.removeGradientOnText();
      }
    },
    onShapeTile(option) {
      if (option.value === 'curve') {
        this.applyCurveOnText();
      } else {
        this.removeTextShape();
      }
    },
    toggleTextColorPicker(e) {
      e.stopPropagation();
      this.$store.commit('setIsOpen', !this.$store.getters.getIsOpen);
    },
    openEffectColorPicker(e) {
      e.stopPropagation();
      const opening = !this.isColorPickerOpen;
      if (opening) {
        this.$store.commit('setIsOpen', false); // only one colour popover at a time
        this.effectColorPopover = this.sidePopoverPosition(e.currentTarget, { width: 303, height: 520 });
      }
      this.openColorPicker();
      if (opening) {
        this.$nextTick(() => {
          this.effectColorPopover = this.fitSidePopover(this.$refs.effectColorPopoverEl, this.effectColorPopover);
        });
      }
    },
    openEmojiPicker(e) {
      if (!this.showEmojiPicker) {
        this.emojiPopover = this.sidePopoverPosition(e.currentTarget, { width: 340, height: 400 });
      }
      this.toggleEmojiPicker();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/lp-tokens';
@import '../styles/lp-panel';

.lp-text-panel__banner {
  margin-top: 4px;
}

.lp-text-panel__gradient {
  margin-top: 8px;
}

.lp-aa {
  font-family: Arial, sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  color: $lp-ink;

  &.is-shadow {
    text-shadow: 2px 2px 2px rgba(20, 22, 27, 0.45);
  }

  &.is-outline {
    color: $white;
    -webkit-text-stroke: 1px $lp-ink;
  }
}

.lp-gradient-chip {
  width: 42px;
  height: 16px;
  border-radius: 5px;
  background: linear-gradient(90deg, #1570EF, #22C3A6);
}

.lp-shape-straight {
  font-family: Arial, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: $lp-ink;
}

.lp-shape-curve {
  width: 50px;
  height: 22px;
  overflow: visible;

  &__guide {
    fill: none;
    stroke: #D0D5DD;
    stroke-width: 1.5;
    stroke-dasharray: 3 3;
    stroke-linecap: round;
  }

  text {
    font-family: Arial, sans-serif;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 1px;
    fill: $lp-ink;
  }
}

.emoji-picker-popup {
  position: fixed;
  z-index: 9999;
  --emoji-font-family: "Twemoji Mozilla", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji", "EmojiOne Color", "Android Emoji", sans-serif;
}
</style>
